import type { BoardCellEntry, BoardCells } from "./boardCells";
import { getCellKey } from "./boardCells";
import { isExpanded, type BoardExpandedIds } from "./boardNavigation";

/** How a click changes the selection: ⌘/Ctrl toggles one card, Shift selects a run. */
export type BoardSelectGesture = "toggle" | "range";

interface BoardEntryIndex<TItem> {
    entry: BoardCellEntry<TItem>;
    /** Position in its cell, in visible order. */
    index: number;
}

/** Every rendered copy by placement key, with its position in its cell. */
export type BoardEntryIndexes<TItem> = ReadonlyMap<
    string,
    BoardEntryIndex<TItem>
>;

export const indexBoardEntries = <TItem>(
    cells: BoardCells<TItem>,
): BoardEntryIndexes<TItem> =>
    new Map(
        Array.from(cells.entriesByCell.values()).flatMap((entries) =>
            entries.map((entry, index) => [
                entry.placementKey,
                { entry, index },
            ]),
        ),
    );

const toggleItemId = (itemIds: ReadonlySet<string>, itemId: string) => {
    const next = new Set(itemIds);
    if (next.has(itemId)) {
        next.delete(itemId);
    } else {
        next.add(itemId);
    }
    return next;
};

const isSameCell = <TItem>(
    first: BoardEntryIndex<TItem>,
    second: BoardEntryIndex<TItem>,
) => getCellKey(first.entry.placement) === getCellKey(second.entry.placement);

/** The item ids from one card to another in the same cell, in visible order. */
const getRunItemIds = <TItem>(
    cells: BoardCells<TItem>,
    first: BoardEntryIndex<TItem>,
    second: BoardEntryIndex<TItem>,
) =>
    (cells.entriesByCell.get(getCellKey(first.entry.placement)) ?? [])
        .slice(
            Math.min(first.index, second.index),
            Math.max(first.index, second.index) + 1,
        )
        .map((entry) => entry.itemId);

interface BoardSelectionArgs<TItem> {
    cells: BoardCells<TItem>;
    indexes: BoardEntryIndexes<TItem>;
    selectedItemIds: ReadonlySet<string>;
    /** The card toggled last: where a range starts. */
    anchorKey: string | null;
}

interface SelectBoardCardArgs<TItem> extends BoardSelectionArgs<TItem> {
    placementKey: string;
    gesture: BoardSelectGesture;
}

export interface BoardSelectResult {
    selectedItemIds: ReadonlySet<string>;
    anchorKey: string;
}

/**
 * The selection after a ⌘/Ctrl or Shift click on a card. Toggling flips the
 * card's item and makes the card the anchor. A range adds every card from
 * the anchor to the clicked one, in visible order, when both sit in the same
 * cell; anywhere else it toggles. Selection is per item, so every copy of a
 * selected item reads as selected. Null when the card isn't on the board.
 */
export const selectBoardCard = <TItem>({
    cells,
    indexes,
    selectedItemIds,
    anchorKey,
    placementKey,
    gesture,
}: SelectBoardCardArgs<TItem>): BoardSelectResult | null => {
    const target = indexes.get(placementKey);
    if (target === undefined) {
        return null;
    }
    const anchor =
        gesture === "range" && anchorKey !== null
            ? indexes.get(anchorKey)
            : undefined;
    if (anchor === undefined || !isSameCell(anchor, target)) {
        return {
            selectedItemIds: toggleItemId(selectedItemIds, target.entry.itemId),
            anchorKey: placementKey,
        };
    }
    return {
        selectedItemIds: new Set([
            ...selectedItemIds,
            ...getRunItemIds(cells, anchor, target),
        ]),
        anchorKey: anchor.entry.placementKey,
    };
};

interface ExtendBoardSelectionArgs<TItem> extends BoardSelectionArgs<TItem> {
    /** The card focus left. */
    fromKey: string;
    /** The card Shift+arrow moved focus to. */
    toKey: string;
}

/**
 * The selection after Shift+arrow moved focus from one card to another.
 * Within a cell it adds the run from the anchor to the new card, starting
 * at the card focus left when the anchor is in another cell or there is
 * none. Into another cell (Shift+Left or Right, or past a lane's end) it
 * adds both cards and the new one becomes the anchor. Never deselects.
 */
export const extendBoardSelection = <TItem>({
    cells,
    indexes,
    selectedItemIds,
    anchorKey,
    fromKey,
    toKey,
}: ExtendBoardSelectionArgs<TItem>): BoardSelectResult | null => {
    const from = indexes.get(fromKey);
    const to = indexes.get(toKey);
    if (from === undefined || to === undefined) {
        return null;
    }
    const anchor = anchorKey === null ? undefined : indexes.get(anchorKey);
    const start =
        anchor !== undefined && isSameCell(anchor, to) ? anchor : from;
    if (!isSameCell(start, to)) {
        return {
            selectedItemIds: new Set([
                ...selectedItemIds,
                from.entry.itemId,
                to.entry.itemId,
            ]),
            anchorKey: toKey,
        };
    }
    return {
        selectedItemIds: new Set([
            ...selectedItemIds,
            ...getRunItemIds(cells, start, to),
        ]),
        anchorKey: start.entry.placementKey,
    };
};

/** Every item with a card in an expanded column and lane: what ⌘A selects. */
export const getExpandedItemIds = <TItem>(
    cells: BoardCells<TItem>,
    expanded: BoardExpandedIds,
): ReadonlySet<string> =>
    new Set(
        Array.from(cells.entriesByCell.values()).flatMap((entries) =>
            entries
                .filter((entry) => isExpanded(expanded, entry.placement))
                .map((entry) => entry.itemId),
        ),
    );
